import moment from 'moment-timezone';
import { createSelector } from 'reselect';
import { selectCurrentClient } from '../Client/selectors';
import {
  Organization,
  Panel,
  panelSchema,
  panelLinkedEntities,
  PanelSubscriptionSettings,
  PANEL_SUBSCRIPTION_SETTINGS_ENTITY_NAME,
  panelSubscriptionSettingsSchema,
  subscriptionSettingsLinkedEntities,
} from './definitions';
import {
  createSingleEntitySelector,
  EntitiesGlobalState,
  createAllEntitiesSelector,
  createDenormalizeSelector,
} from '../modules/entities';
import { OrganizationGlobalState, ORGANIZATION_MODULE_KEY } from './reducer';
import { schema } from 'normalizr';
import {
  participantTagsSchema,
  PARTICIPANT_TAGS_ENTITY_NAME,
  participantTagsLinkedEntities,
  ParticipantTag,
} from 'store/Participant';
import { ClientGlobalState } from 'store/Client';

export const currentOrganizationIdSelector = (state: OrganizationGlobalState) =>
  state[ORGANIZATION_MODULE_KEY].currentOrganizationId;

export const selectCurrentOrganization = createSelector(
  currentOrganizationIdSelector,
  selectCurrentClient,
  (organizationId, client): Organization | undefined => {
    if (!client || !client.organizations || !client.organizations.length) {
      return undefined;
    }
    if (!organizationId) {
      return client.organizations[0];
    }
    return client.organizations.find(
      (organization) => organization.id === organizationId,
    );
  },
);

export const currentPanelIdSelector = (state: OrganizationGlobalState) =>
  state[ORGANIZATION_MODULE_KEY].currentPanel;

export const definedCurrentPanelSelector = createSingleEntitySelector<
  EntitiesGlobalState & OrganizationGlobalState,
  Panel
>(panelSchema, currentPanelIdSelector, panelLinkedEntities);

export const panelsSelector = createSelector(
  selectCurrentOrganization,
  (organization?: Organization): Panel[] => {
    if (!organization || !organization.panels || !organization.panels.length) {
      return [];
    }
    const panels = organization.panels;
    return panels.sort((a: Panel, b: Panel) =>
      moment(b.createdAt).diff(moment(a.createdAt)),
    );
  },
);

export const selectCurrentPanel = createSelector(
  definedCurrentPanelSelector,
  panelsSelector,
  (panel?: Panel, panels?: Panel[]): Panel | undefined => {
    if (panel) {
      return panel;
    }
    if (!panels) {
      return undefined;
    }
    return panels[0];
  },
);

export const panelByIdSelector = createDenormalizeSelector<Panel>(
  panelSchema,
  panelLinkedEntities,
);

export const haveMultiPanelSelector = createSelector(
  panelsSelector,
  (panels) => panels.length > 1,
);

export const panelSubscriptionSettingByPanelIdSelector = createSelector(
  (_: any, panelId?: string) => panelId,
  createAllEntitiesSelector<PanelSubscriptionSettings>(
    PANEL_SUBSCRIPTION_SETTINGS_ENTITY_NAME,
    subscriptionSettingsLinkedEntities,
    new schema.Array(panelSubscriptionSettingsSchema),
  ),
  (panelId: string | undefined, settings: PanelSubscriptionSettings[]) => {
    if (!panelId) {
      return undefined;
    }
    return settings.find(
      (setting: PanelSubscriptionSettings) => setting.panelId === panelId,
    );
  },
) as any;

export const panelSubscriptionSettingSelector = (
  state: EntitiesGlobalState & ClientGlobalState & OrganizationGlobalState,
): PanelSubscriptionSettings | undefined => {
  const panel = selectCurrentPanel(state);
  if (!panel) {
    return undefined;
  }
  return panelSubscriptionSettingByPanelIdSelector(state, panel.id);
};

export const panelSubscriptionSettingsByIdSelector =
  createDenormalizeSelector<PanelSubscriptionSettings>(
    panelSubscriptionSettingsSchema,
    subscriptionSettingsLinkedEntities,
  );

export const createPanelByIdSelector = () =>
  createDenormalizeSelector<Panel>(panelSchema, panelLinkedEntities);

export const allTagsSelector = createAllEntitiesSelector<ParticipantTag>(
  PARTICIPANT_TAGS_ENTITY_NAME,
  participantTagsLinkedEntities,
  new schema.Array(participantTagsSchema),
);

export const tagsByPanelIdSelector = createSelector(
  allTagsSelector,
  (_: any, input?: string) => input,
  (tags: ParticipantTag[], panelId?: string) => {
    if (!panelId) {
      return [];
    }
    return tags.filter((t) => t.panelId === panelId);
  },
) as any;

export const tagIdsMapSelector = createSelector(
  allTagsSelector,
  (tags: ParticipantTag[]): { [tagId: string]: ParticipantTag } => {
    const map: { [tagId: string]: ParticipantTag } = {};

    tags.forEach((tag) => {
      map[tag.id] = tag;
    });

    return map;
  },
);
